import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { fetchData, fetchTAMData } from "./fetchData";
import { RA } from "./RA";
import { TAM } from "./TAM";
import "./Auction.css";
import { Auction, TAM as TAMType } from "../../../models/auction";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { NewDynamicTab } from "../../../components/Page";

const TABS = ["Reverse Auction", "TAM"];

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

const toSafeDate = (value: unknown, fallback: Date): Date => {
  if (isValidDate(value)) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (isValidDate(parsed)) return parsed;
  }
  return fallback;
};

export const formatDateString = (date: Date | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return ""; // Return empty string or a default date format if date is invalid
  }
  return date.toLocaleDateString("en-GB").split("/").reverse().join("-");
};

export function AuctionPage() {
  const [forecastData, setForecastData] = useState<Auction[]>([]);
  const [TAMData, setTAMData] = useState<TAMType[]>([]);
  const [RALoading, setRALoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const fetchRequestIdRef = useRef(0);

  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);

  const defaultDatesState = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setMonth(start.getMonth() - 6);
    return {
      "Reverse Auction": {
        type: "range",
        defaultRangeDate: {
          startDate: new Date(start),
          endDate: new Date(now),
          key: "selection",
        },
      },
      TAM: {
        type: "range",
        defaultRangeDate: {
          startDate: new Date(start),
          endDate: new Date(now),
          key: "selection",
        },
      },
    };
  }, []);

  const auctionActiveTab = TABS.includes(activeTab)
    ? activeTab
    : "Reverse Auction";
  const fallbackRange = defaultDatesState["Reverse Auction"].defaultRangeDate;
  const currentDateObj = DefaultDates?.[auctionActiveTab]?.defaultRangeDate;

  const dateRange = useMemo(() => {
    const startDate = toSafeDate(
      currentDateObj?.startDate,
      fallbackRange.startDate,
    );
    const endDate = toSafeDate(currentDateObj?.endDate, fallbackRange.endDate);
    return startDate.getTime() <= endDate.getTime()
      ? { startDate, endDate }
      : { startDate: endDate, endDate: startDate };
  }, [
    currentDateObj?.startDate,
    currentDateObj?.endDate,
    fallbackRange.startDate,
    fallbackRange.endDate,
  ]);

  const startKey = dateRange.startDate?.getTime();
  const endKey = dateRange.endDate?.getTime();

  useEffect(() => {
    if (!startKey || !endKey) return;
    const requestId = ++fetchRequestIdRef.current;
    const startDate = new Date(startKey);
    const endDate = new Date(endKey);
    console.log("[Auction] fetch start", requestId, startDate, endDate);
    setRALoading(true);
    setLoading(true);
    Promise.all([
      fetchData({ startDate, endDate }),
      fetchTAMData({ startDate, endDate }),
    ])
      .then(([raData, tamData]) => {
        if (fetchRequestIdRef.current !== requestId) return;
        setForecastData(raData ?? []);
        const sortedTamData = (tamData ?? [])
          .slice()
          .sort(
            (a, b) =>
              new Date(a.date ?? "").getTime() -
              new Date(b.date ?? "").getTime(),
          );
        setTAMData(sortedTamData);
      })
      .catch((e) => {
        console.log("[Auction] fetch error", requestId, e);
        if (fetchRequestIdRef.current !== requestId) return;
        setForecastData([]);
        setTAMData([]);
      })
      .finally(() => {
        console.log(
          "[Auction] fetch finally",
          requestId,
          "ref:",
          fetchRequestIdRef.current,
        );
        if (fetchRequestIdRef.current !== requestId) return;
        setRALoading(false);
        setLoading(false);
      });
  }, [startKey, endKey]);

  useEffect(() => {
    const handleCopyCutPaste = (e: any) => {
      e.preventDefault();
    };

    document.addEventListener("copy", handleCopyCutPaste);
    document.addEventListener("cut", handleCopyCutPaste);
    document.addEventListener("paste", handleCopyCutPaste);

    return () => {
      document.removeEventListener("copy", handleCopyCutPaste);
      document.removeEventListener("cut", handleCopyCutPaste);
      document.removeEventListener("paste", handleCopyCutPaste);
    };
  }, []);

  return (
    <NewDynamicTab
      headerHeight="h-[68px]"
      defaultDates={defaultDatesState}
      header="TAM and DEEP"
      tabs={{
        "Reverse Auction": (
          <RA
            forecastData={forecastData}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            loading={RALoading}
          />
        ),
        TAM: (
          <TAM
            TAMData={TAMData}
            loading={loading}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
          />
        ),
      }}
    />
  );
}
